import classNames from 'clsx';
import { get, range } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import CasinoGameHeader from '@swf/components/CasinoGameHeader/CasinoGameHeader';
import CasinoGameList from '@swf/components/CasinoGameList/CasinoGameList';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import { Header } from '@swf/core/enums';
import { select } from '@swf/core/store';
import type { CasinoGame, StreamData } from '@swf/core/types/CasinoGame';
import type { WidgetFC, WidgetInstanceProps } from '@swf/core/types/Widget';
import { asPage, buildPayload, gamesPerColumn, isVisibleByAuth } from '@swf/core/utils/casino';
import type { CasinoMosaicLayout } from '@swf/generated/types/common/Casino';
import { CasinoIcon } from '@swf/generated/types/common/Casino';
import type { CasinoServiceParameters } from '@swf/generated/types/common/CasinoParams';
import type {
  Config as CasinoMosaicWidgetConfig,
  CasinoMosaicWidgetServices
} from '@swf/generated/types/widgets/CasinoMosaicWidget';

import styles from './CasinoMosaicWidget.scss';

export interface Data {
  games1: StreamData<CasinoGame>;
  games2?: StreamData<CasinoGame>;
  games3?: StreamData<CasinoGame>;
}

export type Props = WidgetInstanceProps<CasinoMosaicWidgetConfig, CasinoMosaicWidgetServices, Data>;

interface LocalConfig {
  layout: CasinoMosaicLayout;
  columnConfigs: ColumnConfig[];
}

interface ColumnConfig {
  gamesPer: number;
  serviceName: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  stream: Nullable<Function>;
}

const createGameStream = (dataStream?: StreamData<CasinoGame>): Function => {
  const copy = dataStream?.games.slice(0) || [];
  return (packetSize: number) => copy.splice(0, packetSize);
};

const CasinoMosaicWidget: WidgetFC<Props> = ({
  config,
  data = {} as Data,
  fetchService,
  services,
  style
}) => {
  const containerRef = React.createRef<HTMLDivElement>();
  const sliderRef = React.createRef<SliderHandles>();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [completed, setCompleted] = useState<boolean>(false);
  const showVegasContent = useSelector(select.site.showVegasContent);
  const siteName = useSelector(select.site.getName);
  const currency = useSelector(select.currency.get);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const indexRange = range(1, 4);
  const { pages } = config;

  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });
  const { title } = config;

  const [activeServicesLoaded, setActiveServicesLoaded] = useState<boolean>(false);
  const [realGames, setRealGames] = useState<CasinoGame[]>([]);

  const pageGames = useMemo(() => [asPage(realGames)], [realGames]);

  const localConfig = useMemo(() => {
    const layout = { enabled: true };
    const columnConfigs = indexRange.map((i) => {
      const conf = {
        gamesPer: gamesPerColumn((layout[`section${i}`] = config[`column${i}Layout`])),
        serviceName: config[`column${i}Service`]
      };
      return conf;
    });

    return { layout, columnConfigs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]) as LocalConfig;

  useEffect(() => {
    const reducer = (acc: boolean, key: string): boolean =>
      acc &&
      !!get(data, key, false) &&
      isVisibleByAuth(
        get(services, `${key}.config.onLoad`, false),
        get(services, `${key}.config.onAuth`, false),
        get(services, `${key}.config.onAnonymous`, false),
        isAuthenticated
      );
    const result = localConfig.columnConfigs.map((cf) => cf.serviceName).reduce(reducer, true);
    setActiveServicesLoaded(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.games1, data?.games2, data?.games3, isAuthenticated]);

  useEffect(() => {
    activeServicesLoaded && loadMore(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, isAuthenticated]);

  useEffect(() => {
    if (activeServicesLoaded) {
      const games = [] as CasinoGame[];
      let streamEmpty = false;

      const streams = {};

      localConfig.columnConfigs.forEach(
        (colConfig) =>
          (colConfig.stream =
            streams[colConfig.serviceName] ||
            (streams[colConfig.serviceName] = createGameStream(data[colConfig.serviceName])))
      );

      range(0, pages).forEach((p) => {
        localConfig.columnConfigs.forEach((column) => {
          if (streamEmpty || !column.stream) {
            return;
          }
          const packet = column.stream(column.gamesPer);

          packet?.length === column.gamesPer ? games.push(...packet) : (streamEmpty = true);
        });
        if (p + 1 === pages && !streamEmpty) {
          setCompleted(true);
        }
      });

      setRealGames(games);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServicesLoaded, data.games1, data.games2, data.games3]);

  const loadMore = useCallback(
    async (page: number) => {
      // Service listings
      const servicesToLoad = {} as Record<string, string>;
      localConfig.columnConfigs.forEach(
        (conf) => (servicesToLoad[conf.serviceName] = conf.serviceName)
      );

      Promise.all(
        Object.keys(servicesToLoad).map((serviceName: string) => {
          const { meta, params } = buildPayload({
            activeFilter: 0,
            filters: '',
            showVegasContent,
            siteName,
            defaultParams: get(services, `${serviceName}.params`, {}) as CasinoServiceParameters
          });
          return fetchService(
            serviceName,
            { ...params, pg: page, patch: page > 0 },
            { ...meta, page, patch: page > 0 }
          );
        })
      );

      setCurrentPage(page);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteName, services, buildPayload, fetchService]
  );

  useEffect(() => {
    if (activeServicesLoaded && !completed && !sliderState.right && sliderState.left) {
      loadMore(currentPage + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServicesLoaded, sliderState.left, sliderState.right, completed]);

  const minimumGames = localConfig.columnConfigs.reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (result: number, value: any) => value.gamesPer + result,
    0
  );

  const showWidget = activeServicesLoaded && realGames.length >= minimumGames;

  return showWidget ? (
    <section className={classNames(styles.container)} ref={containerRef} style={style}>
      <Vary by={[Header.Country, Header.Currency]} />
      <CasinoGameHeader
        {...config}
        slide={true}
        title={title}
        data={pageGames}
        stickyHeader={false}
        filterURL=""
        onFilter={(ignore) => false}
        sliderRef={sliderRef}
        sliderState={sliderState}
        filters=""
        icon={CasinoIcon.icnSquare}
        widgetRef={containerRef}
        isValid={() => true}
      />
      <CasinoGameList
        preload={true}
        games={realGames}
        slide={true}
        icon={CasinoIcon.icnSquare}
        mosaic={localConfig.layout}
        sliderRef={sliderRef}
        onScroll={setSliderState}
      />
    </section>
  ) : null;
};

export default CasinoMosaicWidget;
